import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, HelpCircle, ArrowRight, Users, Shield, Clock, Award, Zap, Quote, Phone, X } from 'lucide-react';
import { SAP_B1_LOGO, AWS_BADGE_LOGO, AWS_SMB_LOGO } from '../components/partnerLogos';
import axios from 'axios';

// ─── Pricing tables ───────────────────────────────────────────────────────────
const PRICING = {
  standard: [
    { max: 24, price: 80 }, { max: 49, price: 75 }, { max: 74, price: 70 },
    { max: 99, price: 65 }, { max: 124, price: 60 }, { max: 149, price: 55 },
    { max: Infinity, price: 50 }
  ],
  advanced: [
    { max: 24, price: 90 }, { max: 49, price: 85 }, { max: 74, price: 80 },
    { max: 99, price: 75 }, { max: 124, price: 70 }, { max: 149, price: 65 },
    { max: Infinity, price: 60 }
  ],
  lite: [
    { max: 24, price: 50 }, { max: 49, price: 45 }, { max: 74, price: 40 },
    { max: 99, price: 35 }, { max: 124, price: 30 }, { max: 149, price: 25 },
    { max: Infinity, price: 20 }
  ]
};

function getUnitPrice(type, count) {
  if (!count || count <= 0) return 0;
  return PRICING[type].find(t => count <= t.max)?.price ?? PRICING[type].at(-1).price;
}

// ─── Feature rows ─────────────────────────────────────────────────────────────
const FEATURES = [
  { name: 'Cloud Architecture', whatItMeans: 'The type of cloud environment your SAP Business One system runs on. Standard is a fully managed shared environment - cost-efficient, secure and high-performance. Advanced is a dedicated private environment where your system has its own isolated resources.', whyItMatters: 'Most businesses run perfectly well on Standard. If your business has complex processing, strict compliance requirements, or you need complete isolation, Advanced gives you a private environment that is yours alone.', plainEnglish: 'Standard: shared, expertly managed cloud. Advanced: your own private environment - no shared resources, total isolation.', standard: 'Multi-tenant', advanced: 'Dedicated / Private' },
  { name: 'SAP Business One on AWS Hosting', whatItMeans: 'Your entire SAP Business One system - database, application server, and all your data - runs on Amazon Web Services infrastructure managed entirely by Ascarii. You simply log in via your browser or desktop client.', whyItMatters: 'No servers to buy, no IT team needed to keep SAP running. Ascarii manages everything - infrastructure, backups, monitoring, security patches - so your team focuses entirely on your business.', plainEnglish: 'Your SAP system lives in the cloud. We look after every part of it. You just log in and work.', standard: 'Included', advanced: 'Included' },
  { name: 'SAP B1 Web Client Ready', whatItMeans: 'SAP Business One is available via a modern web browser through the SAP Web Client. Users can access the full SAP B1 experience in Chrome, Edge or other browsers on any device - no desktop software installation required.', whyItMatters: 'Eliminates the need to manage and maintain desktop SAP client installations across your organisation. Users can access SAP from any device, anywhere, without IT involvement to set up or update software. Works seamlessly with your Ascarii-hosted environment and is particularly powerful when combined with AWS WorkSpaces.', plainEnglish: 'Your team can open SAP Business One in their web browser - just like a website. No software to install, no updates to push out, no compatibility issues. Log in and work.', standard: 'Included', advanced: 'Included' },
  { name: 'Multi-Factor Authentication (Okta)', whatItMeans: 'Enterprise-grade Multi-Factor Authentication is included at no extra cost, powered by Okta - a leading identity and access management platform that is SAP-approved. When users log in, they verify their identity with a second factor such as a mobile app or hardware token, preventing unauthorised access even if a password is compromised.', whyItMatters: 'Cyber attacks targeting SAP systems are increasing. MFA is now a baseline requirement for cyber insurance and many regulatory frameworks. Enterprise-grade MFA via Okta is typically charged as a separate licence - with AscariiRun it is included. Your SAP Business One is protected from day one at no additional cost.', plainEnglish: 'Your SAP Business One is protected by enterprise-grade two-factor authentication - included. No extra licence, no separate setup project, SAP-approved security from day one.', standard: 'Included', advanced: 'Included' },
  { name: 'AWS WorkSpaces', whatItMeans: 'AWS WorkSpaces is a fully managed virtual desktop service from Amazon Web Services. Users get a high-performance cloud desktop environment hosted on AWS, giving them access to SAP Business One and associated tools from any device - including thin clients, tablets and older or low-spec machines.', whyItMatters: 'Provides a consistent, high-performance user experience regardless of what hardware your team is using. Ideal for remote workers, contractors, or businesses with mixed or older devices. The desktop runs on AWS servers rather than the user\'s device, so performance is always reliable, security is centrally managed, and IT support overhead is reduced.', plainEnglish: 'Your team gets a fast, fully managed cloud desktop for SAP - from any device, anywhere. Perfect for remote workers or businesses with mixed hardware. Performance lives in AWS, not on the user\'s laptop.', standard: 'Included', advanced: 'Included' },
  { name: 'Cloud Environment Design & Build', whatItMeans: 'Before your system goes live, Ascarii designs and builds your complete cloud environment from scratch - servers, networking, security, backup policies and monitoring. This is included in your subscription.', whyItMatters: 'Other SAP partners typically charge £5,000 to £15,000 as a one-off project fee just to build the cloud environment. With AscariiRun, there is no project, no invoice - we just get on with it.', plainEnglish: 'Moving to cloud or switching partner? We build your new environment as part of the service. No setup fee, no project proposal, no surprises.', standard: 'Included', advanced: 'Included' },
  { name: 'Data Migration Service', whatItMeans: 'We migrate all your existing SAP Business One data - customers, suppliers, stock, financial history, transactions and documents - from your old environment to your new Ascarii-hosted system.', whyItMatters: 'Data migration is where things can go wrong with SAP moves. Ascarii manages this as a defined part of the service. No risk of data loss, no manual re-entry, no separate migration project invoice.', plainEnglish: 'All your SAP data - years of history, every customer, every item - moves across cleanly. Nothing lost. Nothing charged extra.', standard: 'Included', advanced: 'Included' },
  { name: 'Discovery & Design Workshops', whatItMeans: 'A series of structured sessions - calls, workshops and on-site visits where needed - where Ascarii gets to know your business, your processes, your team and your goals before any configuration or migration work begins.', whyItMatters: 'SAP implementations fail when the partner does not understand the customer. We invest the time upfront to understand how your business actually works. There is no cap on sessions - we take whatever time is needed to get it right.', plainEnglish: 'Before we touch anything, we spend real time understanding your business. As many sessions as it takes - calls, workshops, site visits - all included.', standard: 'Unlimited sessions', advanced: 'Unlimited sessions' },
  { name: 'Onboarding Workshops', whatItMeans: 'Structured training and handholding sessions to get your team confident and productive on SAP Business One from day one. Delivered as part of your transition - not as a separate billable training package.', whyItMatters: 'A system is only as valuable as how well your team uses it. Proper onboarding means faster adoption, fewer support calls, and a team that gets real value from SAP from the start.', plainEnglish: 'Your team gets proper, structured training - not a manual and good luck. Included as part of getting you live.', standard: 'Included', advanced: 'Included' },
  { name: 'Unlimited Business Support', whatItMeans: 'Call or email our SAP-certified support team as many times as you need during core hours (8am to 6pm, Monday to Friday). For urgent issues, our team also covers evenings and weekends at no extra charge.', whyItMatters: 'Traditional SAP support contracts charge per incident or cap the number of calls. With AscariiRun, there are no limits and no per-call charges. If you have a question, just ask.', plainEnglish: 'Need help? Call us. Email us. As many times as you like. We never say you have used up your support allowance.', standard: '8am - 6pm + priority evenings & weekends', advanced: '8am - 6pm + priority evenings & weekends' },
  { name: '24x7 Hosting Monitoring', whatItMeans: 'Automated and human monitoring of your SAP Business One infrastructure around the clock, every day of the year including Bank Holidays. Alerts are triaged and responded to immediately.', whyItMatters: 'Most SAP hosting issues happen outside business hours. When your system has a problem at 2am on a Sunday, Ascarii is already aware and working on it before you arrive on Monday morning.', plainEnglish: 'We watch your system 24 hours a day, 7 days a week. Issues get spotted and fixed before they affect your business.', standard: 'Included', advanced: 'Included' },
  { name: 'SAP B1 Upgrades per Year', whatItMeans: 'When SAP releases a major Business One upgrade, Ascarii plans, tests and executes the upgrade for you - included in your subscription. Standard customers are upgraded on Ascarii\'s managed schedule, which allows us to plan and coordinate upgrades efficiently across the shared environment. Advanced customers can choose when their upgrade takes place - giving you full control over timing to fit your business.', whyItMatters: 'Most SAP partners charge £15,000 to £50,000 per upgrade. The cost is so high that many customers skip upgrades entirely and fall years behind. With AscariiRun, staying current is just part of the service - and Advanced customers get the additional benefit of scheduling upgrades on their own terms.', plainEnglish: 'Standard: we upgrade you on our schedule - no disruption, no invoice. Advanced: we upgrade you when you decide - full control, still no invoice.', standard: '1 per year (Ascarii schedule)', advanced: '1 per year (your schedule)' },
  { name: 'Upgrade Testing Environment', whatItMeans: 'Before any SAP upgrade goes live, Ascarii provisions a full working copy of your environment running the new version. Your team can test all processes, reports and add-ons against real data before anything changes on your live system.', whyItMatters: 'Most SAP cloud providers charge extra for upgrade testing environments. AscariiRun includes 30 days every year. You go live with confidence, not fingers crossed.', plainEnglish: 'Before your upgrade goes live, you get a safe copy to test on. Your team checks everything works. You only go live when you are happy.', standard: '30 days / year', advanced: '30 days / year' },
  { name: 'Standard Add-On Delivery', whatItMeans: 'Installation, configuration and deployment of standard SAP Business One add-ons as part of your onboarding or during an upgrade cycle. This consultancy work is included in your subscription.', whyItMatters: 'Most SAP partners charge day rates for add-on implementation. Add-ons can take days to configure correctly. AscariiRun includes this for standard add-ons so you are not hit with unexpected consultancy bills.', plainEnglish: 'Need a standard add-on set up or reconfigured? Raise it with your account manager. Consider it done.', standard: 'Included', advanced: 'Included' },
  { name: 'Advanced Add-On Delivery', whatItMeans: 'Installation, configuration and ongoing management of complex specialist add-ons such as Beas Manufacturing, Produmex WMS and Produmex Scan - solutions that require deep technical expertise.', whyItMatters: 'Complex add-ons can take weeks to implement and cost tens of thousands with other partners. Advanced customers have these managed as part of their subscription - one of the key reasons to choose the Advanced tier.', plainEnglish: 'Running Beas or Produmex? We set it up, support it, upgrade it and manage it - all as part of your subscription.', standard: '\u2013', advanced: 'Included' },
  { name: 'SAP Business One Databases', whatItMeans: 'Each database is a separate SAP Business One company file. These can be used for live production, test and development environments, subsidiary companies, or separate legal entities.', whyItMatters: 'Having a test database is essential for training new staff, testing configurations, and validating upgrades without touching your live data. Standard includes 2, Advanced includes 4 - additional databases can be added.', plainEnglish: 'Multiple SAP company files - one for live, one for testing. Advanced gives you more for subsidiaries or additional environments.', standard: '2 included', advanced: '4 included' },
  { name: 'Infrastructure Generation Upgrades', whatItMeans: 'When Amazon Web Services releases a new generation of server hardware, Ascarii migrates your SAP Business One environment to the latest generation as part of the standard service. No charge, no project to manage, no disruption.', whyItMatters: 'On-premise, a hardware refresh means buying new servers, running a migration project and spending tens of thousands of pounds. Most on-premise customers go four to five years between refreshes on outdated hardware. AWS typically delivers a 10 to 15 per cent performance improvement per generation. With AscariiRun, those improvements arrive automatically.', plainEnglish: 'When AWS releases newer, faster servers, your system moves to them. No project, no cost, no disruption. In the on-premise world that is a full hardware refresh costing tens of thousands. Here, it just happens.', standard: 'Included', advanced: 'Included' },
  { name: 'Dedicated Account Manager', whatItMeans: 'A named person at Ascarii who knows your business, your SAP configuration, your team and where you are heading. Not a helpdesk ticket queue - a specific person you can call or email directly.', whyItMatters: 'Having one point of contact who truly understands your setup makes every interaction faster and more effective. Your account manager proactively reviews your account and ensures the service is working for you.', plainEnglish: 'One person. Knows your business. Picks up the phone. Takes responsibility. No explaining yourself every time you call.', standard: 'Included', advanced: 'Included' },
  { name: 'SAP Training Centre', whatItMeans: 'An online library of self-service SAP Business One training resources - videos, guides and how-to content - available to all your users at any time, from any device. Launching in 2026.', whyItMatters: 'Onboarding new staff is expensive when it relies on experienced users stopping their work to train newcomers. The SAP Training Centre lets new users get up to speed independently and gives all users a refresher resource.', plainEnglish: 'A training library your whole team can access anytime. New starter? Send them to the training centre. Coming in 2026, included.', standard: 'Included (2026)', advanced: 'Included (2026)' },
  { name: 'Minimum Users', whatItMeans: 'Standard is available from a single user - ideal for small teams. Advanced requires a minimum of 20 users due to the dedicated private infrastructure model.', whyItMatters: 'Standard gives any size business access to the full AscariiRun service. Advanced is designed for larger or more complex operations where dedicated infrastructure makes a material difference.', plainEnglish: 'Any size team can start with Standard. Advanced needs at least 20 users to justify the dedicated private environment.', standard: 'From 1 user', advanced: 'From 20 users' },
];

// ─── Cell renderer ────────────────────────────────────────────────────────────
const Cell = ({ value }) => {
  if (value === 'Included' || value === 'Unlimited sessions') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-sm font-medium">
        <CheckCircle2 className="w-3.5 h-3.5" /> {value}
      </span>
    );
  }
  if (value === '–') {
    return <span className="text-slate-400 text-sm font-medium">Not included</span>;
  }
  return <span className="text-slate-700 text-sm font-medium">{value}</span>;
};

// ─── Main component ───────────────────────────────────────────────────────────
const AscariiRunV2Landing = () => {
  const [tier, setTier] = useState('standard');
  const [activeModal, setActiveModal] = useState(null);
  const [directUsers, setDirectUsers] = useState('');
  const [liteUsers, setLiteUsers] = useState('');
  const [currentHosting, setCurrentHosting] = useState('');
  const [currentUpgrade, setCurrentUpgrade] = useState('');
  const [currentSupport, setCurrentSupport] = useState('');
  const [currentMigration, setCurrentMigration] = useState('');
  const [formData, setFormData] = useState({ firstName: '', lastName: '', company: '', email: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSubNav, setShowSubNav] = useState(true);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Calculator
  const direct = parseInt(directUsers) || 0;
  const lite = parseInt(liteUsers) || 0;
  const directPrice = getUnitPrice(tier, direct);
  const litePrice = getUnitPrice('lite', lite);
  const directMonthly = directPrice * direct;
  const liteMonthly = litePrice * lite;
  const totalMonthly = directMonthly + liteMonthly;
  const totalAnnual = totalMonthly * 12;
  const hasResult = direct > 0 || lite > 0;
  const advancedMinError = tier === 'advanced' && direct > 0 && direct < 20;

  // Savings calculator
  const hosting = parseInt(currentHosting) || 0;
  const upgrade = parseInt(currentUpgrade) || 0;
  const support = parseInt(currentSupport) || 0;
  const migration = parseInt(currentMigration) || 0;
  const traditionalAnnual = (hosting * 12) + upgrade + support;
  const hasSavingsInput = hosting > 0 || upgrade > 0 || support > 0;
  const saving1yr = (traditionalAnnual + migration) - totalAnnual;
  const saving3yr = (traditionalAnnual * 3 + migration) - (totalAnnual * 3);
  const saving5yr = (traditionalAnnual * 5 + migration) - (totalAnnual * 5);

  const scrollToForm = () => {
    document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/forms/contact`, {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: `AscariiRun enquiry - Tier: ${tier}, Direct users: ${direct}, Lite users: ${lite}, Estimated monthly: £${totalMonthly.toLocaleString()}`,
        source: 'AscariiRun Landing Page'
      });
      setSubmitted(true);
    } catch {
      alert('Something went wrong. Please try again or call us on +44 (0)1789 777 466.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans" data-testid="ascariirun-landing">

        {/* ── Stripped Header ───────────────────────────────────────────────── */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0F1729]/95 backdrop-blur-md border-b border-white/10" data-testid="landing-header">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
            <img src="/ascariirun-logo.svg" alt="AscariiRun by Ascarii" className="h-12 w-auto" />
            <button onClick={scrollToForm} data-testid="header-cta" className="bg-[#F7A21A] hover:bg-[#e5940a] text-[#0F1729] font-bold px-3 py-2 sm:px-5 sm:py-2.5 rounded-lg text-xs sm:text-sm transition-colors shadow-lg whitespace-nowrap">
              <span className="sm:hidden">Get a price</span>
              <span className="hidden sm:inline">Get my AscariiRun™ price</span>
            </button>
          </div>
          {/* Sticky sub-nav */}
          {showSubNav && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
              className="border-t border-white/10 bg-[#0F1729]/98">
              <div className="max-w-6xl mx-auto px-4 sm:px-6">

                {/* Desktop: horizontal links */}
                <nav className="hidden sm:flex items-center gap-1 py-2">
                  {[
                    { label: 'Why AscariiRun™', href: '#why-ascariirun' },
                    { label: "What's included", href: '#whats-included' },
                    { label: 'Pricing examples', href: '#pricing-examples' },
                    { label: 'Calculator', href: '#calculator' },
                    { label: 'FAQ', href: '#faqs' },
                    { label: 'Talk to us', href: '#get-started' },
                  ].map(link => (
                    <a key={link.href} href={link.href}
                      className="whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-semibold text-slate-400 hover:text-white hover:bg-white/10 transition-all">
                      {link.label}
                    </a>
                  ))}
                </nav>

                {/* Mobile: Jump to dropdown */}
                <div className="sm:hidden py-2 relative">
                  <button
                    onClick={() => setShowMobileNav(v => !v)}
                    className="flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold text-slate-400 hover:text-white hover:bg-white/10 transition-all w-full justify-between"
                    data-testid="mobile-nav-toggle"
                    aria-label="Jump to section"
                  >
                    <span>Jump to...</span>
                    <svg className={`w-3.5 h-3.5 transition-transform ${showMobileNav ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showMobileNav && (
                    <div className="absolute top-full left-0 right-0 bg-[#0F1729] border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden mt-1">
                      {[
                        { label: 'Why AscariiRun™', href: '#why-ascariirun' },
                        { label: "What's included", href: '#whats-included' },
                        { label: 'Pricing examples', href: '#pricing-examples' },
                        { label: 'Calculator', href: '#calculator' },
                        { label: 'FAQ', href: '#faqs' },
                        { label: 'Talk to us', href: '#get-started' },
                      ].map(link => (
                        <a key={link.href} href={link.href}
                          onClick={() => setShowMobileNav(false)}
                          className="block px-5 py-3 text-sm text-slate-300 hover:text-white hover:bg-white/10 transition-all border-b border-white/5 last:border-0">
                          {link.label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>

              </div>
            </motion.div>
          )}
        </header>

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section className="pt-16 min-h-screen bg-[#0F1729] text-white flex items-center" data-testid="hero-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 lg:py-28">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#29ABE2]/20 border border-[#29ABE2]/40 rounded-full text-[#29ABE2] text-sm font-medium mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#29ABE2] inline-block"></span>
                  SAP Business One - Fully Managed
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight mb-6">
                  Run SAP Business One for one monthly price. <span className="block mt-2 text-[#F7A21A]">Everything included.</span>
                </h1>
                <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                  AscariiRun™ bundles cloud hosting, unlimited support, upgrades, migrations and more into one simple per-user, per-month price. Trusted by businesses across the UK with a 4.9/5 support rating. You focus on running your business. We handle the rest.
                </p>
                <ul className="space-y-3 mb-10">
                  {[
                    'Cloud hosting on AWS - fully managed, 24x7 monitored',
                    'Unlimited support included - no call limits, no extra charges',
                    'Upgrades included - planned, tested and delivered by us',
                    'Discovery and migration included - however long it takes',
                    'SAP B1 Web Client Ready - access via any modern browser',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-200">
                      <CheckCircle2 className="w-5 h-5 text-[#29ABE2] flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={scrollToForm}
                  data-testid="hero-cta"
                  className="inline-flex items-center gap-2 bg-[#F7A21A] hover:bg-[#e5940a] text-[#0F1729] font-bold px-8 py-4 rounded-lg text-base transition-all shadow-xl hover:shadow-2xl hover:scale-105"
                >
                  Get my AscariiRun™ price <ArrowRight className="w-4 h-4" />
                </button>
                <div className="mt-4 flex items-center gap-4">
                  <a href="#calculator" className="inline-flex items-center gap-1.5 text-[#29ABE2] hover:text-white text-sm font-semibold transition-colors">
                    Try the pricing calculator <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
                <p className="text-xs text-slate-500 mt-3">SAP software and add-on licences are the only addition. No hidden extras.</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="lg:block">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
                  <p className="text-slate-400 text-sm font-medium mb-6 uppercase tracking-wide">What's included in every plan</p>
                  <div className="space-y-4">
                    {[
                      { icon: Shield, label: 'AWS Cloud Hosting', sub: '99.995% uptime SLA' },
                      { icon: Zap, label: 'Unlimited Support', sub: '4.9/5 customer satisfaction' },
                      { icon: Clock, label: 'Annual Upgrades', sub: '30 days testing included' },
                      { icon: Users, label: 'Dedicated Account Manager', sub: 'One contact, always' },
                      { icon: Award, label: 'SAP Premier Partner', sub: 'Multiple SAP awards · 15+ years experience' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-4 p-3 bg-white/5 rounded-xl border border-white/10">
                        <div className="w-10 h-10 rounded-lg bg-[#29ABE2]/20 flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-5 h-5 text-[#29ABE2]" />
                        </div>
                        <div>
                          <p className="text-white font-semibold text-sm">{item.label}</p>
                          <p className="text-slate-400 text-xs">{item.sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* ── Phone CTA strip — above the fold ───────────────────────── */}
            <div className="mt-10 pt-8 border-t border-white/10">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center sm:text-left">
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <Phone className="w-4 h-4 text-[#F7A21A] flex-shrink-0" />
                  <span>Prefer to speak to a real person? Call us now:</span>
                </div>
                <a href="tel:01789777466"
                  className="inline-flex items-center gap-2 text-white font-bold text-lg hover:text-[#F7A21A] transition-colors tracking-wide"
                  aria-label="Call Ascarii on 01789 777 466">
                  01789 777 466
                </a>
                <span className="hidden sm:block text-slate-400 text-sm">Mon - Fri, 8am - 6pm</span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Lionweld Kennedy Testimonial — hero slot ─────────────────────── */}
        <section className="bg-[#0F1729] pb-10 px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} viewport={{ once: true }}
              className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <Quote className="w-8 h-8 text-[#29ABE2] mb-4 opacity-60" />
              <p className="text-white text-lg font-medium leading-relaxed">
                "Having the ERP solution on AWS is more secure so it's easier for us to achieve the required level on our <strong className="text-[#29ABE2]">internal audits for IT and system security.</strong> From the Group point of view, AWS was the way they wanted us to go."
              </p>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#29ABE2]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#29ABE2] text-sm font-bold">KW</span>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Karl Wrightson</p>
                  <p className="text-slate-400 text-xs">Operations Director, Lionweld Kennedy Group</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── Partner Credentials ─────────────────────────────────────────── */}
        <section className="bg-[#0a1020] border-t border-white/10 py-10" data-testid="trust-strip">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-center text-slate-500 text-xs font-semibold uppercase tracking-widest mb-8">Official Partner Credentials</p>
            <div className="flex flex-wrap justify-center items-stretch gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">

              <div className="flex-1 min-w-[180px] flex flex-col items-center justify-center gap-1.5 px-8 py-5 bg-[#0a1020]">
                <span className="text-slate-400 text-[10px] font-semibold uppercase tracking-widest">Amazon Web Services</span>
                <span className="text-white text-sm font-bold">Advanced Tier Partner</span>
              </div>

              <div className="flex-1 min-w-[180px] flex flex-col items-center justify-center gap-1.5 px-8 py-5 bg-[#0a1020] border-x border-white/10">
                <span className="text-slate-400 text-[10px] font-semibold uppercase tracking-widest">SAP Business One</span>
                <span className="text-[#F7A21A] text-base font-bold">Premier Partner</span>
              </div>

              <div className="flex-1 min-w-[180px] flex flex-col items-center justify-center gap-1.5 px-8 py-5 bg-[#0a1020]">
                <span className="text-slate-400 text-[10px] font-semibold uppercase tracking-widest">Amazon Web Services</span>
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-bold">SMB Competency</span>
                  <span className="bg-[#FF9900] text-white text-[7px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide leading-none">New</span>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── Nodding Dog — Upgrade Pain ──────────────────────────────────── */}
        <section className="py-20 bg-white">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">

            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ duration: 0.4 }} viewport={{ once: true }}
              className="text-[#29ABE2] text-xs font-bold uppercase tracking-widest mb-10">
              Does this sound familiar?
            </motion.p>

            <div className="space-y-5">
              {[
                "You pay your SAP maintenance every year.",
                "It entitles you to the latest version of the software.",
                "But upgrading costs another £15,000. So you put it off.",
                "Then a bug forces your hand - and you pay it anyway.",
                "You find the new version looks just like the old one.",
                "You didn't even get the new features switched on.",
              ].map((line, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
                  className="flex items-start gap-4">
                  <div className="w-1 flex-shrink-0 mt-1.5 h-4 rounded-full bg-slate-200" />
                  <p className="text-slate-700 text-lg leading-snug">{line}</p>
                </motion.div>
              ))}
            </div>

            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }} viewport={{ once: true }}
              className="mt-14 pt-12 border-t border-slate-100">
              <p className="text-2xl sm:text-3xl font-bold text-slate-900 leading-snug mb-2">
                The technology budget you didn't spend on upgrades
              </p>
              <p className="text-2xl sm:text-3xl font-bold leading-snug">
                <span className="text-[#F7A21A]">could be working for you instead.</span>
              </p>
              <p className="text-slate-500 text-base mt-6 mb-8">There is a better way.</p>
              <a href="#get-started"
                className="inline-flex items-center gap-2 bg-[#F7A21A] hover:bg-[#e5940a] text-[#0F1729] font-bold px-7 py-3.5 rounded-lg text-sm transition-all shadow-lg">
                Discover AscariiRun™ <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

          </div>
        </section>

        {/* ── Cost Comparison ──────────────────────────────────────────────── */}
        <section className="py-20 bg-white" id="why-ascariirun" data-testid="cost-comparison-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-full text-sm font-semibold mb-4">The hidden cost of the status quo</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Running SAP Business One has been expensive for too long.</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">Traditional SAP partners invoice separately for every service. Here is what businesses are typically paying - and what they are missing out on as a result.</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-10">
              {/* Traditional costs */}
              <div className="bg-red-50 border border-red-200 rounded-2xl p-8" data-testid="traditional-costs">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-red-500 text-base font-bold">✕</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">What traditional partners charge</h3>
                </div>
                <div className="space-y-4">
                  {[
                    { item: 'Cloud environment design & build', cost: '£5,000 - £15,000', note: 'One-off project fee, before you even go live' },
                    { item: 'Annual SAP B1 upgrade', cost: '£15,000 - £50,000', note: 'Per upgrade - many customers skip upgrades entirely' },
                    { item: 'Upgrade test environment', cost: 'Charged separately', note: 'On top of the upgrade fee - often a surprise invoice' },
                    { item: 'Cloud hosting (50-user system)', cost: '£5,000 - £10,000/month', note: '£60,000 - £120,000 per year for hosting alone' },
                    { item: 'Support contract', cost: '£10,000 - £20,000+/year', note: 'Varies by licence count - separate annual invoice' },
                  ].map((row, i) => (
                    <div key={i} className="flex items-start gap-3 pb-4 border-b border-red-200 last:border-0 last:pb-0">
                      <div className="flex-1">
                        <p className="text-slate-900 font-medium text-sm">{row.item}</p>
                        <p className="text-slate-500 text-xs mt-0.5">{row.note}</p>
                      </div>
                      <span className="text-red-600 font-bold text-sm text-right flex-shrink-0 ml-2">{row.cost}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* AscariiRun */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8" data-testid="ascariirun-costs">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">With AscariiRun™</h3>
                </div>
                <div className="space-y-4">
                  {[
                    { item: 'Cloud environment design & build', note: 'We design and build your environment - included' },
                    { item: 'Annual SAP B1 upgrade', note: 'Planned, tested and delivered by Ascarii - included' },
                    { item: 'Upgrade test environment (30 days/year)', note: 'Provisioned every year - included' },
                    { item: 'Cloud hosting on AWS', note: '99.995% uptime, 24x7 monitored - included' },
                    { item: 'Unlimited support', note: '8am-6pm core hours, evenings & weekends for priority - included' },
                  ].map((row, i) => (
                    <div key={i} className="flex items-start gap-3 pb-4 border-b border-emerald-200 last:border-0 last:pb-0">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-slate-900 font-medium text-sm">{row.item}</p>
                        <p className="text-slate-500 text-xs mt-0.5">{row.note}</p>
                      </div>
                      <span className="text-emerald-600 font-bold text-sm flex-shrink-0 ml-2">Included</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-[#F7A21A]/10 border border-[#F7A21A]/30 rounded-xl">
                  <p className="text-amber-800 font-bold text-sm">One monthly per-user price. No capital costs. No project fees. No upgrade invoices.</p>
                  <p className="text-slate-500 text-xs mt-1">SAP software and add-on licences are the only addition.</p>
                </div>
              </div>
            </div>

            {/* Frozen in time callout */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8" data-testid="frozen-callout">
              <h3 className="text-xl font-bold text-amber-800 mb-4">Businesses are being frozen in time - and it is costing them more than money.</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { heading: 'Stuck on old versions', body: 'High upgrade costs mean many businesses are running versions of SAP B1 that are years out of date - missing every improvement SAP has shipped since.' },
                  { heading: 'Add-ons held back too', body: 'New features in Beas Manufacturing, Produmex WMS and other add-ons often require the latest SAP B1. If you cannot afford the upgrade, you cannot access those features either.' },
                  { heading: 'Paying twice for upgrades', body: 'Customers pay SAP maintenance to have the right to upgrade - then pay their partner again to actually do it. AscariiRun™ ends that. Upgrades are included.' },
                  { heading: 'Cloud migrations shelved', body: 'Businesses want to move to the cloud but the design, build and migration costs are prohibitive. AscariiRun™ includes the migration - combine it with an upgrade at no extra cost.' },
                  { heading: 'New projects blocked', body: 'Running a new project that needs the latest version? If you cannot afford the upgrade, the project stalls. AscariiRun™ keeps you current so your business keeps moving.' },
                  { heading: 'SAP is investing - you should benefit', body: 'SAP is spending millions advancing SAP Business One. Those improvements are sitting on the shelf for businesses who cannot afford to upgrade. That needs to change.' },
                ].map((point, i) => (
                  <div key={i} className="bg-white border border-amber-100 rounded-xl p-4">
                    <p className="text-slate-900 font-semibold text-sm mb-1">{point.heading}</p>
                    <p className="text-slate-600 text-xs leading-relaxed">{point.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Feature Table ────────────────────────────────────────────────── */}
        <section className="py-20 bg-sky-50/60" id="whats-included" data-testid="features-table-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 bg-[#29ABE2]/10 text-[#29ABE2] rounded-full text-sm font-semibold mb-4">What's included</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">One price. Everything included.</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">Every AscariiRun™ plan bundles everything your SAP Business One system needs. No surprises. No add-ons. No hidden extras.</p>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
              <table className="w-full min-w-[600px]" data-testid="features-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-500 uppercase tracking-wide w-1/2">Feature</th>
                    <th className="text-center px-6 py-4 w-1/4">
                      <span className="inline-block px-5 py-2 text-white rounded-full text-sm font-bold shadow-md" style={{ background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)' }}>Standard</span>
                    </th>
                    <th className="text-center px-6 py-4 w-1/4">
                      <span className="inline-block px-5 py-2 text-white rounded-full text-sm font-bold shadow-md" style={{ background: 'linear-gradient(135deg, #4776e6 0%, #8e54e9 100%)' }}>Advanced</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURES.map((row, i) => (
                    <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-blue-50/30 transition-colors`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-800 text-sm font-medium">{row.name}</span>
                          <button
                            onClick={() => setActiveModal(row)}
                            className="text-slate-400 hover:text-[#29ABE2] transition-colors flex-shrink-0"
                            data-testid={`modal-trigger-${i}`}
                            aria-label={`Learn more about ${row.name}`}
                          >
                            <HelpCircle className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center"><Cell value={row.standard} /></td>
                      <td className="px-6 py-4 text-center"><Cell value={row.advanced} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-emerald-50 border-t-2 border-emerald-200">
                    <td colSpan={3} className="px-6 py-4 text-sm text-emerald-700">
                      <span className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                        Everything shown as Included above is covered by your AscariiRun™ per-user, per-month price. SAP software and add-on licences are the only addition.
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </section>

        {/* ── Calculator ───────────────────────────────────────────────────── */}
        {/* ── Pricing Examples ─────────────────────────────────────────────── */}
        <section className="py-20 bg-white" id="pricing-examples">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-14">
              <span className="inline-block px-4 py-1.5 bg-[#29ABE2]/10 text-[#29ABE2] rounded-full text-sm font-semibold mb-4">Typical pricing</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">What does AscariiRun™ cost in practice?</h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">Four real-world examples based on common business profiles. Use the calculator below to work out your own.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  label: 'Small business',
                  desc: 'Straightforward SAP B1, core office team',
                  tier: 'Standard',
                  lines: ['10 Standard users', '£80/user'],
                  monthly: '£800',
                  annual: '£9,600',
                  accent: 'border-[#29ABE2]/30 bg-[#29ABE2]/3',
                  badge: 'bg-[#29ABE2]/10 text-[#29ABE2]',
                },
                {
                  label: 'Growing business',
                  desc: 'SAP B1 direct users plus warehouse operatives',
                  tier: 'Standard + Lite',
                  lines: ['25 Standard users', '£75/user', '10 Lite users', '£50/user'],
                  monthly: '£2,375',
                  annual: '£28,500',
                  accent: 'border-[#29ABE2]/30',
                  badge: 'bg-[#29ABE2]/10 text-[#29ABE2]',
                },
                {
                  label: 'Mid-size operation',
                  desc: 'Complex SAP B1 with add-ons plus shopfloor team',
                  tier: 'Advanced + Lite',
                  lines: ['50 Advanced users', '£80/user', '20 Lite users', '£50/user'],
                  monthly: '£5,000',
                  annual: '£60,000',
                  accent: 'border-[#F7A21A]/30',
                  badge: 'bg-[#F7A21A]/15 text-[#b57a10]',
                },
                {
                  label: 'Scale-up enterprise',
                  desc: 'Multi-site, advanced add-ons, large indirect team',
                  tier: 'Advanced + Lite',
                  lines: ['100 Advanced users', '£70/user', '50 Lite users', '£40/user'],
                  monthly: '£9,000',
                  annual: '£108,000',
                  accent: 'border-[#F7A21A]/30',
                  badge: 'bg-[#F7A21A]/15 text-[#b57a10]',
                },
              ].map((ex, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
                  className={`bg-white rounded-2xl border-2 ${ex.accent} p-6 flex flex-col`}>
                  <span className={`self-start px-3 py-1 rounded-full text-xs font-semibold mb-4 ${ex.badge}`}>{ex.tier}</span>
                  <h3 className="text-slate-900 font-bold text-base mb-1">{ex.label}</h3>
                  <p className="text-slate-500 text-xs mb-5 leading-relaxed flex-1">{ex.desc}</p>

                  {/* User breakdown */}
                  <div className="space-y-1.5 mb-5 pb-5 border-b border-slate-100">
                    {ex.lines.reduce((acc, line, idx) => {
                      if (idx % 2 === 0) acc.push([line, ex.lines[idx + 1]]);
                      return acc;
                    }, []).map(([users, rate], j) => (
                      <div key={j} className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">{users}</span>
                        <span className="text-slate-400">{rate}</span>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div>
                    <div className="flex items-end justify-between mb-1">
                      <span className="text-slate-500 text-xs">Monthly</span>
                      <span className="text-2xl font-bold text-slate-900">{ex.monthly}</span>
                    </div>
                    <div className="flex items-end justify-between">
                      <span className="text-slate-400 text-xs">Annually</span>
                      <span className="text-sm font-semibold text-[#F7A21A]">{ex.annual}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <p className="text-center text-slate-400 text-xs mt-8">All prices exclude SAP software and add-on licences. Prices vary by user count - use the calculator below for your exact figures.</p>
          </div>
        </section>

        {/* ── Calculator ───────────────────────────────────────────────────── */}
        <section className="py-20 bg-slate-50" id="calculator" data-testid="calculator-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-[#29ABE2]/10 text-[#29ABE2] rounded-full text-sm font-semibold mb-4">Indicative pricing</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">What would AscariiRun™ cost for your team?</h2>
              <p className="text-slate-600">Use the calculator below to get an indicative monthly and annual estimate. Our team will build a detailed quote based on your exact requirements.</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-md overflow-hidden" data-testid="calculator">
              {/* Step 1 */}
              <div className="p-6 sm:p-8 border-b border-slate-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-7 h-7 rounded-full bg-[#29ABE2] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">1</div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm">Choose your system type</h3>
                    <p className="text-slate-500 text-xs mt-0.5">Not sure? Our team will advise - both options are fully managed by Ascarii.</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { value: 'standard', label: 'AscariiRun™ Standard', sub: 'Multi-tenant cloud - ideal for straightforward SAP B1 installations with lightweight or no add-ons. From 1 user.', from: '£50 - £80/user/month' },
                    { value: 'advanced', label: 'AscariiRun™ Advanced', sub: 'Dedicated private cloud - for complex SAP B1 systems with add-ons like Beas Manufacturing or Produmex WMS. From 20 users.', from: '£60 - £90/user/month' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { setTier(opt.value); setDirectUsers(''); }}
                      data-testid={`tier-${opt.value}`}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${tier === opt.value ? 'border-[#29ABE2] bg-[#29ABE2]/5' : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-bold text-slate-900 text-sm">{opt.label}</span>
                        {tier === opt.value && <CheckCircle2 className="w-4 h-4 text-[#29ABE2]" />}
                      </div>
                      <p className="text-slate-500 text-xs leading-relaxed mb-2">{opt.sub}</p>
                      <span className="text-xs font-semibold text-[#0F1729]">{opt.from}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 2 */}
              <div className="p-6 sm:p-8 border-b border-slate-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-7 h-7 rounded-full bg-[#29ABE2] text-white text-sm font-bold flex items-center justify-center flex-shrink-0">2</div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm">How many direct SAP B1 users do you have?</h3>
                    <p className="text-slate-500 text-xs mt-0.5">These are people who log directly into SAP Business One to do their work.</p>
                  </div>
                </div>
                <input
                  type="number"
                  min={tier === 'advanced' ? '20' : '1'}
                  placeholder={tier === 'advanced' ? 'Minimum 20 for Advanced' : 'e.g. 15'} inputMode="numeric"
                  value={directUsers}
                  onChange={e => setDirectUsers(e.target.value)}
                  data-testid="direct-users-input"
                  className={`w-full sm:w-48 border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2] ${advancedMinError ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                />
                {advancedMinError && (
                  <p className="text-red-500 text-xs mt-2">AscariiRun™ Advanced requires a minimum of 20 users.</p>
                )}
                {directUsers && !advancedMinError && direct > 0 && (
                  <p className="text-slate-500 text-xs mt-2">
                    {direct} users at <strong className="text-slate-700">£{getUnitPrice(tier, direct)}/user/month</strong> = <strong className="text-slate-700">£{directMonthly.toLocaleString()}/month</strong>
                  </p>
                )}
              </div>

              {/* Step 3 */}
              <div className="p-6 sm:p-8 border-b border-slate-100">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-7 h-7 rounded-full bg-[#F7A21A] text-[#0F1729] text-sm font-bold flex items-center justify-center flex-shrink-0">3</div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-sm">Do you have any indirect (Lite) users? <span className="text-slate-400 font-normal">Optional</span></h3>
                    <p className="text-slate-500 text-xs mt-0.5">These are people who don't log directly into SAP B1 - shopfloor workers, handheld device users, Produmex WMS or Scan operators. Priced at £20 - £50/user/month, lower than direct users. Enter 0 or leave blank if none.</p>
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  placeholder="e.g. 10 (or leave blank)" inputMode="numeric"
                  value={liteUsers}
                  onChange={e => setLiteUsers(e.target.value)}
                  data-testid="lite-users-input"
                  className="w-full sm:w-48 border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F7A21A]"
                />
                {liteUsers && lite > 0 && (
                  <p className="text-slate-500 text-xs mt-2">
                    {lite} Lite users at <strong className="text-slate-700">£{getUnitPrice('lite', lite)}/user/month</strong> = <strong className="text-slate-700">£{liteMonthly.toLocaleString()}/month</strong>
                  </p>
                )}
              </div>

              {/* Result — always visible */}
              <div className="p-6 sm:p-8 bg-[#0F1729]" data-testid="calculator-result">
                <div className="text-white">
                  <p className="text-slate-400 text-xs uppercase tracking-wide font-semibold mb-4">Your indicative estimate</p>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Monthly</p>
                      <p className="text-4xl font-bold text-white">
                        {hasResult && !advancedMinError ? `£${totalMonthly.toLocaleString()}` : '—'}
                      </p>
                      <p className="text-slate-500 text-xs mt-1">per month</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Annually</p>
                      <p className="text-4xl font-bold text-[#F7A21A]">
                        {hasResult && !advancedMinError ? `£${totalAnnual.toLocaleString()}` : '—'}
                      </p>
                      <p className="text-slate-500 text-xs mt-1">per year</p>
                    </div>
                  </div>
                  {advancedMinError && (
                    <p className="text-red-400 text-xs mt-3">AscariiRun™ Advanced requires a minimum of 20 users.</p>
                  )}
                  {hasResult && !advancedMinError && (
                    <div className="mt-4 pt-4 border-t border-white/10 text-xs text-slate-400 space-y-1">
                      {direct > 0 && <p>{direct} {tier.charAt(0).toUpperCase() + tier.slice(1)} user{direct !== 1 ? 's' : ''} at £{getUnitPrice(tier, direct)}/user = £{directMonthly.toLocaleString()}/month</p>}
                      {lite > 0 && <p>{lite} Lite user{lite !== 1 ? 's' : ''} at £{getUnitPrice('lite', lite)}/user = £{liteMonthly.toLocaleString()}/month</p>}
                    </div>
                  )}
                  {!hasResult && !advancedMinError && (
                    <p className="text-slate-500 text-xs mt-4">Enter your user numbers above to see your estimate.</p>
                  )}
                  {hasResult && !advancedMinError && (
                    <p className="text-slate-500 text-xs mt-4">Indicative estimate only - excludes SAP software and add-on licences. Contact us for a detailed quote tailored to your exact setup.</p>
                  )}

                  {/* Savings section — always visible */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 pt-6 border-t border-white/10"
                    data-testid="savings-section"
                  >
                    <p className="text-slate-300 text-sm font-semibold mb-4">See how much you could save - enter your current costs below.</p>
                    <div className="grid sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5">Current hosting (£/month)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                          <input type="number" min="0" placeholder="e.g. 7000" value={currentHosting} onChange={e => setCurrentHosting(e.target.value)} data-testid="savings-hosting" className="w-full bg-white/10 border border-white/20 rounded-lg pl-7 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]" />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Monthly cloud hosting charge</p>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5">Annual upgrade cost (£)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                          <input type="number" min="0" placeholder="e.g. 25000" value={currentUpgrade} onChange={e => setCurrentUpgrade(e.target.value)} data-testid="savings-upgrade" className="w-full bg-white/10 border border-white/20 rounded-lg pl-7 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]" />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Enter 0 if you are not currently upgrading</p>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5">Annual support cost (£)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                          <input type="number" min="0" placeholder="e.g. 15000" value={currentSupport} onChange={e => setCurrentSupport(e.target.value)} data-testid="savings-support" className="w-full bg-white/10 border border-white/20 rounded-lg pl-7 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]" />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Annual SAP support contract</p>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5">Cloud migration cost (£) <span className="text-slate-500">optional</span></label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                          <input type="number" min="0" placeholder="e.g. 10000" value={currentMigration} onChange={e => setCurrentMigration(e.target.value)} data-testid="savings-migration" className="w-full bg-white/10 border border-white/20 rounded-lg pl-7 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]" />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">One-off cost if you still need to migrate</p>
                      </div>
                    </div>

                    {hasSavingsInput && hasResult && !advancedMinError ? (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4" data-testid="savings-result">
                        <p className="text-slate-400 text-xs uppercase tracking-wide font-semibold mb-4">Your estimated savings with AscariiRun™</p>
                        <div className="grid grid-cols-3 gap-3">
                          {[
                            { label: '1 year', saving: saving1yr, traditional: traditionalAnnual + migration },
                            { label: '3 years', saving: saving3yr, traditional: traditionalAnnual * 3 + migration },
                            { label: '5 years', saving: saving5yr, traditional: traditionalAnnual * 5 + migration },
                          ].map((col, i) => (
                            <div key={i} className={`rounded-xl p-4 text-center border ${col.saving > 0 ? 'bg-emerald-900/30 border-emerald-700/40' : 'bg-white/5 border-white/10'}`}>
                              <p className="text-slate-400 text-xs mb-2">{col.label}</p>
                              <p className="text-xs text-slate-500 mb-1">Traditional: <span className="text-slate-300">£{col.traditional.toLocaleString()}</span></p>
                              <p className="text-xs text-slate-500 mb-2">AscariiRun: <span className="text-[#F7A21A]">£{(totalAnnual * (i === 0 ? 1 : i === 1 ? 3 : 5)).toLocaleString()}</span></p>
                              <div className={`text-xl font-bold ${col.saving > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {col.saving > 0 ? '+' : ''}£{Math.abs(col.saving).toLocaleString()}
                              </div>
                              <p className="text-xs mt-1 text-slate-500">{col.saving > 0 ? 'saved' : 'more expensive'}</p>
                            </div>
                          ))}
                        </div>
                        <p className="text-slate-500 text-xs mt-4">Indicative comparison only. Traditional costs based on figures you have entered. AscariiRun costs exclude SAP software and add-on licences.</p>
                      </motion.div>
                    ) : (
                      <p className="text-slate-500 text-xs">
                        {!hasResult ? 'Enter your user count above, then add your current costs to see your savings.' : 'Enter your current costs above to see your potential savings.'}
                      </p>
                    )}
                  </motion.div>

                  <div className="mt-6 flex flex-col gap-3">
                    <button
                      onClick={scrollToForm}
                      className="inline-flex items-center justify-center gap-2 bg-[#F7A21A] hover:bg-[#e5940a] text-[#0F1729] font-bold px-6 py-3 rounded-lg text-sm transition-all w-full sm:w-auto"
                    >
                      Get my AscariiRun™ price <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Natgraph Testimonial ─────────────────────────────────────────── */}
        <section className="py-14 bg-slate-800">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} viewport={{ once: true }}
              className="bg-white/10 border border-white/15 rounded-2xl p-8">
              <Quote className="w-7 h-7 text-[#29ABE2] mb-4 opacity-70" />
              <p className="text-white text-base font-medium leading-relaxed">
                "The AWS platform is secure and reputable. We're looking at <strong className="text-[#F7A21A]">longevity</strong> and we felt that we would get better support working with a global cloud provider."
              </p>
              <div className="mt-5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#29ABE2]/25 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#29ABE2] text-sm font-bold">WH</span>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Warren Hatfield</p>
                  <p className="text-slate-400 text-xs">Finance Director, Natgraph</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────────── */}
        <section className="py-20 bg-slate-50 border-t border-slate-200" id="faqs">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-[#29ABE2]/10 text-[#29ABE2] rounded-full text-sm font-semibold mb-4">Common questions</span>
              <h2 className="text-3xl font-bold text-slate-900">Before you get in touch</h2>
            </div>
            <div className="space-y-4">
              {[
                { q: "What contract terms are available?", a: "AscariiRun™ is available on contract terms from 12 to 36 months. Choosing a longer term gives you price lock-in for the full period, protecting your business from price increases. CPI-linked adjustments may apply in line with our terms and conditions. Longer contracts also give both parties greater confidence for planning ahead. At contract end, you can renew or discuss your options with your account manager. Your SAP Business One licences are held directly with SAP and remain yours throughout." },
                { q: "Who owns our data?", a: "You do. Your SAP Business One data is yours at all times. It is hosted in your chosen AWS region and you can request a full export of your data at any point. Ascarii does not use your data for any other purpose." },
                { q: "What happens as we grow and add more users?", a: "Your monthly price adjusts as you add users. The per-user rate moves down as your user count increases - so growth reduces your unit cost. You simply notify your account manager and your subscription adjusts at the next billing period." },
                { q: "How does AscariiRun™ handle our SAP licences?", a: "SAP software licences and add-on licences are purchased separately - they are the only cost outside your AscariiRun™ subscription. Ascarii can advise on the right licence model for your business and handle procurement through our SAP Premier Partner relationship." },
                { q: "Can we move from Standard to Advanced later?", a: "Yes. If your business grows or your requirements become more complex, you can move from the multi-tenant Standard environment to a Dedicated Advanced environment. Ascarii manages the migration with no disruption to your operations." },
              ].map((faq, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }} viewport={{ once: true }}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:border-[#29ABE2]/40 hover:shadow-sm transition-all">
                  <h3 className="text-sm font-bold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Contact Form ─────────────────────────────────────────────────── */}
        <section className="py-20 bg-[#0F1729]" id="get-started" data-testid="contact-form-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="grid lg:grid-cols-2 gap-14 items-start">
              <div>
                <span className="inline-block px-4 py-1.5 bg-[#29ABE2]/20 text-[#29ABE2] rounded-full text-sm font-semibold mb-5">Let's talk</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5">Ready to run SAP Business One the right way?</h2>
                <p className="text-slate-300 mb-8 leading-relaxed">
                  Tell us a little about your business and we'll get back to you with a detailed AscariiRun™ proposal - including a full breakdown of your hosting environment, user counts and pricing. No sales pitch - just a clear, honest quote.
                </p>
                <div className="space-y-5">
                  {[
                    { icon: Clock, title: 'Response within 1 business day', sub: 'We take every enquiry seriously' },
                    { icon: Users, title: 'No-pressure conversation', sub: 'We are here to help you make the right decision' },
                    { icon: Award, title: 'SAP Premier Partner', sub: 'Multiple SAP awards · 15+ years experience' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[#29ABE2]/20 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-[#29ABE2]" />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">{item.title}</p>
                        <p className="text-slate-400 text-xs">{item.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Phone option */}
                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <Phone className="w-4 h-4 text-[#F7A21A] flex-shrink-0" />
                      <span>Prefer to speak to a real person?</span>
                    </div>
                    <a href="tel:01789777466" className="text-white font-bold text-lg hover:text-[#F7A21A] transition-colors tracking-wide">
                      01789 777 466
                    </a>
                  </div>
                  <p className="text-slate-400 text-sm mt-1 ml-6">Mon - Fri, 8am - 6pm</p>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8">
                {submitted ? (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center py-8" data-testid="form-success">
                    <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-white mb-2">Thank you - we'll be in touch.</h3>
                    <p className="text-slate-400 text-sm">A member of the Ascarii team will respond within one business day.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">First name *</label>
                        <input
                          required
                          type="text"
                          data-testid="input-first-name"
                          value={formData.firstName}
                          onChange={e => setFormData(p => ({ ...p, firstName: e.target.value }))}
                          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]"
                          placeholder="Sarah"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-300 mb-1.5">Last name *</label>
                        <input
                          required
                          type="text"
                          data-testid="input-last-name"
                          value={formData.lastName}
                          onChange={e => setFormData(p => ({ ...p, lastName: e.target.value }))}
                          className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]"
                          placeholder="Johnson"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">Company *</label>
                      <input
                        required
                        type="text"
                        data-testid="input-company"
                        value={formData.company}
                        onChange={e => setFormData(p => ({ ...p, company: e.target.value }))}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]"
                        placeholder="Your company name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">Work email *</label>
                      <input
                        required
                        type="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]"
                        placeholder="sarah@yourcompany.com"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1.5">Phone number *</label>
                      <input
                        required
                        type="tel"
                        data-testid="input-phone"
                        value={formData.phone}
                        onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#29ABE2]"
                        placeholder="+44 7700 000000"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="form-submit"
                      className="w-full bg-[#F7A21A] hover:bg-[#e5940a] disabled:opacity-60 text-[#0F1729] font-bold py-3.5 rounded-lg text-sm transition-all shadow-lg"
                    >
                      {loading ? 'Sending...' : 'Get my AscariiRun™ price'}
                    </button>
                    <p className="text-xs text-slate-400 text-center font-medium">No sales pitch. Indicative quote within 1 business day.</p>
                    <p className="text-xs text-slate-500 text-center">We'll never share your details. See our <a href="/privacy-policy" className="underline hover:text-white">privacy policy</a>.</p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── Minimal Footer ───────────────────────────────────────────────── */}
        <footer className="bg-[#0a1020] py-8 border-t border-white/10" data-testid="landing-footer">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <img src="/ascariirun-logo.svg" alt="AscariiRun by Ascarii" className="h-8 w-auto opacity-80" />
            <div className="flex items-center gap-6 text-xs text-slate-500">
              <span>© 2026 Ascarii Ltd. All rights reserved.</span>
              <a href="/privacy-policy" className="hover:text-slate-300 transition-colors">Privacy Policy</a>
              <a href="/terms" className="hover:text-slate-300 transition-colors">Terms</a>
              <a href="https://www.ascarii.com" className="hover:text-slate-300 transition-colors">ascarii.com</a>
            </div>
          </div>
        </footer>

        {/* ── Feature Modal ─────────────────────────────────────────────────── */}
        <AnimatePresence>
          {activeModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[200] flex items-center justify-center p-4"
              onClick={() => setActiveModal(null)}
            >
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.2 }}
                className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full z-10 overflow-hidden"
                onClick={e => e.stopPropagation()}
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-[#29ABE2] to-[#1e8fc4] px-6 py-5 flex items-start justify-between">
                  <h3 className="text-white font-bold text-xl pr-8 leading-snug">{activeModal.name}</h3>
                  <button onClick={() => setActiveModal(null)} className="text-white/80 hover:text-white transition-colors flex-shrink-0 mt-0.5" aria-label="Close">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-6 space-y-5 max-h-[70vh] overflow-y-auto">
                  <div>
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-2">What it means</p>
                    <p className="text-slate-700 text-sm leading-relaxed">{activeModal.whatItMeans}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-2">Why this matters to you</p>
                    <p className="text-slate-700 text-sm leading-relaxed">{activeModal.whyItMatters}</p>
                  </div>
                  <div className="bg-[#29ABE2]/8 border border-[#29ABE2]/20 rounded-xl p-4">
                    <p className="text-[#29ABE2] text-[10px] font-bold uppercase tracking-widest mb-2">In plain English</p>
                    <p className="text-slate-700 text-sm italic leading-relaxed">"{activeModal.plainEnglish}"</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 pb-6">
                  <button
                    onClick={() => setActiveModal(null)}
                    className="w-full bg-gradient-to-r from-[#29ABE2] to-[#1e8fc4] hover:from-[#1e8fc4] hover:to-[#1779a8] text-white font-bold py-3.5 rounded-xl transition-all"
                  >
                    Got it
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
  );
};

export default AscariiRunV2Landing;
